import { addDays, differenceInCalendarDays, parseISO } from 'date-fns';
import { AssetStateCountsResponse } from '@/api/generatedApi';
import { isValidAssetState } from './assetStateDisplayText';

export const TRIAL_CLEANUP_DAYS_AFTER_EXPIRATION = 30;
export interface TrialWarningLevel {
  level: 'normal' | 'warning';
  color: 'info' | 'warning';
}

function getUTCDate(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export function calculateDaysRemaining(trialExpiresAt: Date | string): number {
  const expirationDate = typeof trialExpiresAt === 'string' ? parseISO(trialExpiresAt) : trialExpiresAt;
  const now = new Date();

  const expirationUTC = getUTCDate(expirationDate);
  const nowUTC = getUTCDate(now);

  return differenceInCalendarDays(expirationUTC, nowUTC);
}

export function isTrialExpired(trialExpiresAt: Date | string | undefined): boolean {
  if (!trialExpiresAt) return false;
  return calculateDaysRemaining(trialExpiresAt) <= 0;
}

export function calculateCleanupDate(trialExpiresAt: Date | string): Date {
  const expirationDate = typeof trialExpiresAt === 'string' ? parseISO(trialExpiresAt) : trialExpiresAt;
  return addDays(expirationDate, TRIAL_CLEANUP_DAYS_AFTER_EXPIRATION);
}

export function calculateUploadPercentage(current: number, limit: number | undefined): number {
  if (!limit || limit === 0) return 0;
  return Math.round((current / limit) * 100);
}

export function getTimeWarningLevel(daysRemaining: number): TrialWarningLevel {
  if (daysRemaining <= 3) {
    return { level: 'warning', color: 'warning' };
  }
  if (daysRemaining <= 7) {
    return { level: 'warning', color: 'warning' };
  }
  return { level: 'normal', color: 'info' };
}

export function getUploadWarningLevel(percentage: number): TrialWarningLevel {
  if (percentage >= 95) {
    return { level: 'warning', color: 'warning' };
  }
  if (percentage >= 80) {
    return { level: 'warning', color: 'warning' };
  }
  return { level: 'normal', color: 'info' };
}

export function getOverallWarningLevel(daysRemaining: number, uploadPercentage: number): TrialWarningLevel {
  const timeWarning = getTimeWarningLevel(daysRemaining);
  const uploadWarning = getUploadWarningLevel(uploadPercentage);

  if (timeWarning.level === 'warning' || uploadWarning.level === 'warning') {
    return { level: 'warning', color: 'warning' };
  }
  return { level: 'normal', color: 'info' };
}

export function formatTrialText(daysRemaining: number, uploadsRemaining: number): string {
  if (daysRemaining <= 0) {
    return 'Free trial: Expired';
  }
  return `Free trial: ${daysRemaining} days remaining, ${uploadsRemaining} uploads remaining`;
}

export function calculateTotalAssets(assetCounts?: AssetStateCountsResponse): number {
  return Object.entries(assetCounts || {})
    .filter(([key]) => isValidAssetState(key))
    .reduce((sum, [, count]) => sum + count, 0);
}

export function getDisplayText(daysRemaining: number, uploadsRemaining: number | undefined): string {
  if (uploadsRemaining === undefined) {
    return daysRemaining <= 0 ? 'Free trial: Expired' : `Free trial: ${daysRemaining} days remaining`;
  }
  return formatTrialText(daysRemaining, uploadsRemaining);
}
